/**
 * Agrupamentos padrão do cliente.
 *
 * Um orçamento novo deste cliente já nasce com estes agrupamentos, para que o
 * trabalho comece no preenchimento e não em recriar as seções da planilha à
 * mão. O nome tem de ser o EXATO da seção na aba de custos: é por ele que o
 * mapeamento recorta o bloco na hora de gerar, e um nome fora do lugar não dá
 * erro — o bloco só sai vazio.
 *
 * Reordenar é por setas, não por arrastar: a tabela de mapeamentos ao lado
 * também não arrasta, e a lista é curta (13 seções na planilha da BRF, 20 no
 * PME do SOMOS).
 *
 * "Detectar da planilha" traz as seções que o próprio template declara, com o
 * nome exatamente como está nele — é o jeito de não digitar à mão
 * "AGÊNCIA (NF)  - SERVIÇOS INTERNOS".
 *
 * "Detectar de lista suspensa" é para a planilha que não tem seções: a da Honda
 * é uma tabela única, e o agrupamento de cada linha se escolhe na coluna GRUPO,
 * uma lista com 61 opções. Os agrupamentos pré-estabelecidos são essas opções.
 *
 * "Detectar das abas" é para a planilha com uma aba por agrupamento: a da
 * Fresenius tem AÉREO, TRANSFER, HOSPEDAGENS… cada uma com o mesmo bloco de
 * itens. O nome é o da aba — o título dentro dela tem erro de cópia ("TERRESTRE"
 * na aba TRANSFER) e o da seção é o cabeçalho ("Tipo de AÉREO").
 */
import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import GridOnIcon from "@mui/icons-material/GridOn";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TabIcon from "@mui/icons-material/Tab";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";

import ClientSpreadsheetService from "../../../../../services/clientSpreadsheet.service";
import {
  BudgetGroupTemplateEntry,
  BudgetGroupTemplateValidation,
  ClientSpreadsheetRecord,
  FieldMapping,
  SheetInspection,
  TEMPLATE_CALC_OPTIONS,
  TemplateDropdown,
  TemplateSection,
} from "../../../../../types/clientSpreadsheet.type";
import { CALC_OPTION_SHORT_LABEL } from "../../../Budget/CategoryMoveModals/parts";
import { CalcOption } from "../../../../../types/budget/budget";
import { normalizeGroupName } from "../../../../../utils/budget-groups";
import { sectionTitle } from "../ReplicateDialog/replicate";

type Props = {
  clientId: string;
  canManage: boolean;
  /**
   * Do registro salvo; a edição acontece em cópia local até o "Salvar".
   *
   * Vem opcional de propósito: registro antigo não tem o campo, e um
   * `?? []` no PAI criaria uma lista nova a cada render dele — o efeito abaixo
   * dispararia junto e apagaria o que estivesse sendo digitado.
   */
  savedTemplate?: BudgetGroupTemplateEntry[];
  /** Mapeamentos em tela, para o "Detectar dos mapeamentos". */
  mappings: FieldMapping[];
  validation?: BudgetGroupTemplateValidation;
  /**
   * Seções que o template declara, da aba de custos — a que tem mais seções.
   * Alimenta o "Detectar da planilha" e mostra em que linhas cada agrupamento
   * cai, que é o que o mapeamento vai pedir.
   */
  templateSections?: { sheetName: string; sections: TemplateSection[] } | null;
  /** Listas suspensas de todas as abas, para o "Detectar de lista suspensa". */
  templateDropdowns?: SheetDropdown[];
  /** Uma entrada por aba com seção, para o "Detectar das abas". */
  templateSheets?: SheetBlock[];
};

export type SheetDropdown = TemplateDropdown & { sheetName: string };

/** A aba e o primeiro bloco de itens dela. */
export type SheetBlock = { sheetName: string; section: TemplateSection };

/**
 * As abas que declaram ao menos uma seção, na ordem das abas. Com uma aba só
 * não há "uma aba por agrupamento" — é a aba de custos da BRF ou da Colgate, e
 * o nome dela não é agrupamento —, então devolve vazio.
 */
export function pickTemplateSheets(
  sheets: SheetInspection[] | undefined
): SheetBlock[] {
  const found = (sheets ?? [])
    .filter((sheet) => sheet.sections?.length && sheet.name.trim())
    .map((sheet) => ({
      sheetName: sheet.name,
      section: (sheet.sections as TemplateSection[])[0],
    }));

  return found.length > 1 ? found : [];
}

/** As listas de todas as abas, na ordem das abas e das colunas. */
export function pickTemplateDropdowns(
  sheets: SheetInspection[] | undefined
): SheetDropdown[] {
  return (sheets ?? []).flatMap((sheet) =>
    (sheet.dropdowns ?? []).map((dropdown) => ({
      ...dropdown,
      sheetName: sheet.name,
    }))
  );
}

const dropdownLabel = (dropdown: TemplateDropdown) =>
  dropdown.header?.split("\n")[0].trim() || `Coluna ${dropdown.column}`;

/**
 * A aba de custos é a que declara mais seções. No PME do SOMOS a "Folha de
 * rosto" também tem um bloco com total (o financeiro, "nº OC"), e a
 * "Orçamentos comparativos" repete as 20 seções da "Planilha Master" — o
 * empate fica com a primeira aba, que é a Master.
 */
export function pickTemplateSections(
  sheets: SheetInspection[] | undefined
): { sheetName: string; sections: TemplateSection[] } | null {
  let best: SheetInspection | null = null;
  (sheets ?? []).forEach((sheet) => {
    const count = sheet.sections?.length ?? 0;
    if (count > (best?.sections?.length ?? 0)) best = sheet;
  });

  const chosen = best as SheetInspection | null;
  return chosen?.sections?.length
    ? { sheetName: chosen.name, sections: chosen.sections }
    : null;
}

function sameTemplate(
  a: BudgetGroupTemplateEntry[],
  b: BudgetGroupTemplateEntry[]
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export default function BudgetGroupTemplate({
  clientId,
  canManage,
  savedTemplate,
  mappings,
  validation,
  templateSections,
  templateDropdowns,
  templateSheets,
}: Props) {
  const queryClient = useQueryClient();
  const [entries, setEntries] = useState<BudgetGroupTemplateEntry[]>([]);
  const [dropdownMenu, setDropdownMenu] = useState<HTMLElement | null>(null);

  // Chave de CONTEÚDO, não de identidade. Quem monta a prop pode criar uma
  // lista nova a cada render — registro antigo não tem o campo, e um `?? []`
  // no pai basta — e ressincronizar por identidade apagaria o que estivesse
  // sendo digitado.
  const savedKey = JSON.stringify(savedTemplate ?? []);
  const saved = useMemo(
    () => JSON.parse(savedKey) as BudgetGroupTemplateEntry[],
    [savedKey]
  );

  // O registro chega depois do primeiro render, e volta a chegar a cada save.
  useEffect(() => {
    setEntries(saved.map((e) => ({ ...e, calcOptions: [...e.calcOptions] })));
  }, [saved]);

  const saveMutation = useMutation(
    (payload: BudgetGroupTemplateEntry[]) =>
      ClientSpreadsheetService.saveBudgetGroupTemplate(clientId, payload),
    {
      onSuccess: (updated: ClientSpreadsheetRecord) => {
        queryClient.setQueryData(["client-spreadsheet", clientId], updated);
        queryClient.invalidateQueries([
          "client-spreadsheet-inspection",
          clientId,
        ]);
        toast.success("Agrupamentos padrao salvos.");
      },
      onError: (err: any) => {
        toast.error(
          err?.response?.data?.message ?? "Erro ao salvar os agrupamentos."
        );
      },
    }
  );

  /** Agrupamentos que os mapeamentos citam e que ainda não estão na lista. */
  const detectableGroups = useMemo(() => {
    const known = new Set(entries.map((e) => normalizeGroupName(e.name)));
    const found: string[] = [];
    const seen = new Set<string>();

    mappings.forEach((mapping) => {
      const raw = mapping.group?.trim();
      if (!raw) return;
      const key = normalizeGroupName(raw);
      if (known.has(key) || seen.has(key)) return;
      seen.add(key);
      found.push(raw);
    });

    return found;
  }, [entries, mappings]);

  /**
   * Seções do template que ainda não estão na lista, na ordem da planilha.
   * Entra só o título: na Colgate o do AÉREO traz embaixo três linhas de aviso
   * sobre tarifa, que não são nome de agrupamento.
   */
  const detectableSections = useMemo(() => {
    const known = new Set(entries.map((e) => normalizeGroupName(e.name)));
    const found: string[] = [];

    (templateSections?.sections ?? []).forEach((section) => {
      const title = sectionTitle(section.name);
      const key = normalizeGroupName(title);
      if (!key || known.has(key) || known.has(normalizeGroupName(section.name)))
        return;
      known.add(key);
      found.push(title);
    });

    return found;
  }, [entries, templateSections]);

  /** Abas que ainda não estão na lista, na ordem da planilha. */
  const detectableSheets = useMemo(() => {
    const known = new Set(entries.map((e) => normalizeGroupName(e.name)));
    const found: string[] = [];

    (templateSheets ?? []).forEach(({ sheetName }) => {
      const key = normalizeGroupName(sheetName);
      if (!key || known.has(key)) return;
      known.add(key);
      found.push(sheetName.trim());
    });

    return found;
  }, [entries, templateSheets]);

  const sheetByName = useMemo(() => {
    const index = new Map<string, SheetBlock>();
    (templateSheets ?? []).forEach((sheet) => {
      const key = normalizeGroupName(sheet.sheetName);
      if (key && !index.has(key)) index.set(key, sheet);
    });
    return index;
  }, [templateSheets]);

  /** Por lista, as opções que ainda não estão na lista, na ordem da planilha. */
  const detectableByDropdown = useMemo(() => {
    const known = new Set(entries.map((e) => normalizeGroupName(e.name)));
    return (templateDropdowns ?? []).map((dropdown) => {
      const seen = new Set<string>();
      const names = dropdown.values.filter((value) => {
        const key = normalizeGroupName(value);
        if (!key || known.has(key) || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      return { dropdown, names };
    });
  }, [entries, templateDropdowns]);

  /** A primeira lista em que o nome é opção, para dizer de onde ele veio. */
  const dropdownByName = useMemo(() => {
    const index = new Map<string, SheetDropdown>();
    (templateDropdowns ?? []).forEach((dropdown) => {
      dropdown.values.forEach((value) => {
        const key = normalizeGroupName(value);
        if (key && !index.has(key)) index.set(key, dropdown);
      });
    });
    return index;
  }, [templateDropdowns]);

  const sectionByName = useMemo(() => {
    const index = new Map<string, TemplateSection>();
    (templateSections?.sections ?? []).forEach((section) => {
      [section.name, sectionTitle(section.name)].forEach((name) => {
        const key = normalizeGroupName(name);
        if (key && !index.has(key)) index.set(key, section);
      });
    });
    return index;
  }, [templateSections]);

  const duplicated = useMemo(() => {
    const counts = new Map<string, number>();
    entries.forEach((entry) => {
      const key = normalizeGroupName(entry.name);
      if (!key) return;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    const repeated = new Set<string>();
    counts.forEach((count, key) => {
      if (count > 1) repeated.add(key);
    });
    return repeated;
  }, [entries]);

  const hasEmptyName = entries.some((entry) => !entry.name.trim());
  const hasNoCalcOption = entries.some(
    (entry) => entry.calcOptions.length === 0
  );
  const isDirty = !sameTemplate(entries, saved);
  const canSave =
    canManage &&
    isDirty &&
    !hasEmptyName &&
    !hasNoCalcOption &&
    duplicated.size === 0 &&
    !saveMutation.isLoading;

  function update(index: number, patch: Partial<BudgetGroupTemplateEntry>) {
    setEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, ...patch } : entry))
    );
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;
    setEntries((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function toggleCalcOption(index: number, option: string) {
    const current = entries[index].calcOptions;

    if (current.includes(option)) {
      update(index, { calcOptions: current.filter((o) => o !== option) });
      return;
    }

    // As conhecidas saem na ordem canônica; o que a tela NÃO mostra fica.
    // O endpoint aceita "E", e mexer num chip não pode apagar em silêncio uma
    // forma de cobrança que quem edita nem vê.
    const known = [...TEMPLATE_CALC_OPTIONS].filter(
      (o) => current.includes(o) || o === option
    );
    const hidden = current.filter(
      (o) => !(TEMPLATE_CALC_OPTIONS as readonly string[]).includes(o)
    );

    update(index, { calcOptions: [...known, ...hidden] });
  }

  function addEntry(name = "") {
    setEntries((prev) => [
      ...prev,
      { name, calcOptions: [...TEMPLATE_CALC_OPTIONS] },
    ]);
  }

  function detectFromTemplate() {
    if (detectableSections.length === 0) return;
    setEntries((prev) => [
      ...prev,
      ...detectableSections.map((name) => ({
        name,
        calcOptions: [...TEMPLATE_CALC_OPTIONS],
      })),
    ]);
  }

  function detectFromSheets() {
    if (detectableSheets.length === 0) return;
    setEntries((prev) => [
      ...prev,
      ...detectableSheets.map((name) => ({
        name,
        calcOptions: [...TEMPLATE_CALC_OPTIONS],
      })),
    ]);
  }

  function detectFromDropdown(names: string[]) {
    setDropdownMenu(null);
    if (names.length === 0) return;
    setEntries((prev) => [
      ...prev,
      ...names.map((name) => ({
        name,
        calcOptions: [...TEMPLATE_CALC_OPTIONS],
      })),
    ]);
  }

  function detectFromMappings() {
    if (detectableGroups.length === 0) return;
    setEntries((prev) => [
      ...prev,
      ...detectableGroups.map((name) => ({
        name,
        calcOptions: [...TEMPLATE_CALC_OPTIONS],
      })),
    ]);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
        mb={1}
      >
        <Typography fontWeight="bold" fontSize={15}>
          Agrupamentos padrao ({entries.length})
        </Typography>
        {canManage && (
          <Box display="flex" gap={1} flexWrap="wrap">
            <Tooltip
              title={
                !templateSections?.sections.length
                  ? "Nenhuma secao com total detectada no template"
                  : detectableSections.length > 0
                  ? `Aba "${
                      templateSections.sheetName
                    }": ${detectableSections.join(", ")}`
                  : "Todas as secoes da planilha ja estao na lista"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GridOnIcon />}
                  style={{ textTransform: "none" }}
                  disabled={detectableSections.length === 0}
                  onClick={detectFromTemplate}
                >
                  Detectar da planilha ({detectableSections.length})
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                !templateSheets?.length
                  ? "O template nao tem uma aba por agrupamento"
                  : detectableSheets.length > 0
                  ? `Uma aba por agrupamento: ${detectableSheets.join(", ")}`
                  : "Todas as abas ja estao na lista"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<TabIcon />}
                  style={{ textTransform: "none" }}
                  disabled={detectableSheets.length === 0}
                  onClick={detectFromSheets}
                >
                  Detectar das abas ({detectableSheets.length})
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={
                templateDropdowns?.length
                  ? "Traz as opcoes de uma lista da planilha (ex.: coluna GRUPO)"
                  : "Nenhuma lista suspensa detectada no template"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<ListAltIcon />}
                  style={{ textTransform: "none" }}
                  disabled={!templateDropdowns?.length}
                  aria-haspopup="menu"
                  onClick={(e) => setDropdownMenu(e.currentTarget)}
                >
                  Detectar de lista suspensa ({templateDropdowns?.length ?? 0})
                </Button>
              </span>
            </Tooltip>
            <Menu
              anchorEl={dropdownMenu}
              open={!!dropdownMenu}
              onClose={() => setDropdownMenu(null)}
            >
              {detectableByDropdown.map(({ dropdown, names }) => (
                <MenuItem
                  key={`${dropdown.sheetName}|${dropdown.column}|${dropdown.source}`}
                  disabled={names.length === 0}
                  onClick={() => detectFromDropdown(names)}
                >
                  <ListItemText
                    primary={`${dropdownLabel(dropdown)} — ${
                      names.length
                    } novo(s) de ${dropdown.values.length}`}
                    secondary={`Aba "${dropdown.sheetName}", coluna ${dropdown.column}, linhas ${dropdown.startRow} a ${dropdown.endRow}`}
                  />
                </MenuItem>
              ))}
            </Menu>
            <Tooltip
              title={
                detectableGroups.length > 0
                  ? `Adiciona: ${detectableGroups.join(", ")}`
                  : "Nenhum agrupamento novo nos mapeamentos"
              }
            >
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AutoFixHighIcon />}
                  style={{ textTransform: "none" }}
                  disabled={detectableGroups.length === 0}
                  onClick={detectFromMappings}
                >
                  Detectar dos mapeamentos ({detectableGroups.length})
                </Button>
              </span>
            </Tooltip>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              style={{
                textTransform: "none",
                borderColor: "#1361A4",
                color: "#1361A4",
              }}
              onClick={() => addEntry()}
            >
              Adicionar agrupamento
            </Button>
          </Box>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary">
        Um orcamento novo deste cliente ja nasce com estes agrupamentos. O nome
        precisa ser o mesmo da secao na aba de custos do template — e por ele
        que o mapeamento recorta o bloco. Em planilha sem secoes, use as opcoes
        da lista suspensa do agrupamento (ex.: coluna GRUPO); em planilha com
        uma aba por agrupamento, o nome da aba.
      </Typography>

      {entries.length === 0 ? (
        <Typography color="text.secondary" variant="body2" mt={2}>
          Nenhum agrupamento padrao configurado. Os orcamentos deste cliente
          nascem sem agrupamento, como antes.
        </Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap={1} mt={2}>
          {entries.map((entry, index) => {
            const isDuplicated =
              !!entry.name.trim() &&
              duplicated.has(normalizeGroupName(entry.name));
            const section = sectionByName.get(normalizeGroupName(entry.name));
            const sheet = section
              ? undefined
              : sheetByName.get(normalizeGroupName(entry.name));
            const dropdown =
              section || sheet
                ? undefined
                : dropdownByName.get(normalizeGroupName(entry.name));

            return (
              <Box
                key={index}
                display="flex"
                alignItems="flex-start"
                gap={1}
                flexWrap="wrap"
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ minWidth: 20, mt: 1.2 }}
                >
                  {index + 1}.
                </Typography>

                <TextField
                  size="small"
                  placeholder="Ex: 1 - CENOGRAFIA E EQUIPAMENTOS"
                  value={entry.name}
                  disabled={!canManage}
                  onChange={(e) => update(index, { name: e.target.value })}
                  error={isDuplicated || !entry.name.trim()}
                  helperText={
                    isDuplicated
                      ? "Nome repetido: os dois recortariam o mesmo bloco."
                      : !entry.name.trim()
                      ? "Informe o nome da secao."
                      : section
                      ? `Itens nas linhas ${section.startRow} a ${section.endRow} da aba "${templateSections?.sheetName}"`
                      : sheet
                      ? `Itens nas linhas ${sheet.section.startRow} a ${sheet.section.endRow} da aba "${sheet.sheetName}"`
                      : dropdown
                      ? `Opcao da lista ${dropdownLabel(dropdown)} (coluna ${
                          dropdown.column
                        } da aba "${dropdown.sheetName}")`
                      : undefined
                  }
                  sx={{ flex: "1 1 280px" }}
                  inputProps={{
                    "aria-label": `Nome do agrupamento ${index + 1}`,
                  }}
                />

                <Box display="flex" gap={0.5} alignItems="center" mt={0.5}>
                  {TEMPLATE_CALC_OPTIONS.map((option) => (
                    <Tooltip
                      key={option}
                      title={CALC_OPTION_SHORT_LABEL[option as CalcOption]}
                    >
                      <Chip
                        label={option}
                        size="small"
                        aria-label={`${option} no agrupamento ${index + 1}`}
                        color={
                          entry.calcOptions.includes(option)
                            ? "primary"
                            : "default"
                        }
                        variant={
                          entry.calcOptions.includes(option)
                            ? "filled"
                            : "outlined"
                        }
                        onClick={
                          canManage
                            ? () => toggleCalcOption(index, option)
                            : undefined
                        }
                      />
                    </Tooltip>
                  ))}
                </Box>

                {canManage && (
                  <Box display="flex" mt={0.2}>
                    <IconButton
                      size="small"
                      aria-label={`Subir agrupamento ${index + 1}`}
                      disabled={index === 0}
                      onClick={() => move(index, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Descer agrupamento ${index + 1}`}
                      disabled={index === entries.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      aria-label={`Remover agrupamento ${index + 1}`}
                      onClick={() =>
                        setEntries((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {hasNoCalcOption && (
        <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
          <Typography variant="caption">
            Todo agrupamento precisa de ao menos uma forma de cobranca — sem
            nenhuma ele nao apareceria em lugar nenhum.
          </Typography>
        </Alert>
      )}

      {validation && (
        <Box mt={2} display="flex" flexDirection="column" gap={0.5}>
          {validation.groupsWithoutMapping.length > 0 && (
            <Alert
              severity="info"
              icon={<WarningAmberIcon fontSize="inherit" />}
              sx={{ py: 0 }}
            >
              <Typography variant="caption">
                Sem mapeamento, a secao nasce e fica vazia:{" "}
                <strong>{validation.groupsWithoutMapping.join(", ")}</strong>
              </Typography>
            </Alert>
          )}
          {/*
            Só quando JÁ EXISTE template: divergir precisa de duas listas. Sem
            template cadastrado, criar o agrupamento à mão no orçamento é o
            fluxo normal e sempre foi — alertar ali seria acusar de errado quem
            está trabalhando certo. Quem quiser adotar o template tem o
            "Detectar dos mapeamentos" logo acima, que já mostra a contagem.
          */}
          {saved.length > 0 &&
            validation.groupsWithoutTemplateEntry.length > 0 && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="inherit" />}
                sx={{ py: 0 }}
              >
                <Typography variant="caption">
                  Mapeados mas fora do template — o bloco so enche se alguem
                  criar o agrupamento a mao no orcamento:{" "}
                  <strong>
                    {validation.groupsWithoutTemplateEntry.join(", ")}
                  </strong>
                </Typography>
              </Alert>
            )}
        </Box>
      )}

      {canManage && (
        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button
            size="small"
            style={{ textTransform: "none" }}
            disabled={!isDirty || saveMutation.isLoading}
            onClick={() =>
              setEntries(
                saved.map((e) => ({
                  ...e,
                  calcOptions: [...e.calcOptions],
                }))
              )
            }
          >
            Descartar alteracoes
          </Button>
          <Button
            size="small"
            variant="contained"
            style={{ textTransform: "none" }}
            disabled={!canSave}
            startIcon={
              saveMutation.isLoading ? (
                <CircularProgress size={14} />
              ) : undefined
            }
            onClick={() =>
              saveMutation.mutate(
                entries.map((entry) => ({
                  ...entry,
                  name: entry.name.trim(),
                }))
              )
            }
          >
            Salvar agrupamentos
          </Button>
        </Box>
      )}
    </Paper>
  );
}
